import { Alert, Table } from 'antd'
import dayjs from 'dayjs'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { cx } from '@/Ship/Helpers/cssClass'
import { MILEAGE_DEVIATION_OPTIONS } from '@/Containers/ShiftSection/ShiftContainer/Contracts/MileageDeviation'
import {
    formatKm,
    formatKmDelta,
    formatPercentDelta,
} from '@/Containers/ShiftSection/ShiftContainer/Helpers/shiftFormat'
import { ShiftResource } from '@/Containers/ShiftSection/ShiftContainer/Resources/ShiftResource'

import type { MileageShift } from '../Contracts/Report'
import { useReportFetch } from '../Hooks/useReportFetch'

import styles from './Report.module.css'

/** Ліміт деталізації на бекенді (MileageReportAction::SHIFTS_LIMIT) */
const LIMIT = 500

interface MileageShiftsProps {
    /** id водія або `none` */
    groupKey: string
    params: Record<string, string>
}

/** Зміни водія (розгорнутий рядок «Контролю пробігу»); код відкриває картку зміни */
export const MileageShifts = ({ groupKey, params }: MileageShiftsProps) => {
    const { data, loading, error } = useReportFetch<{ items: MileageShift[] }>(
        '/services/reports/mileage-control/shifts',
        {
            ...params,
            key: groupKey,
        }
    )
    const items = data?.items ?? []

    if (error)
        return (
            <div className={cx(styles, 'drill')}>
                <Alert type="error" showIcon title={error} />
            </div>
        )

    const km = (value: number | null) => <span className={cx(styles, 'num')}>{formatKm(value)}</span>

    const columns = [
        {
            title: 'Дата зміни',
            dataIndex: 'shiftDate',
            key: 'shiftDate',
            width: 110,
            render: (value: string) => dayjs(value).format('DD.MM.YYYY'),
        },
        {
            title: 'Зміна',
            dataIndex: 'code',
            key: 'code',
            render: (code: string, row: MileageShift) => (
                <button
                    type="button"
                    className={cx(styles, 'nameLink')}
                    onClick={() =>
                        OpenResourceCardTask({ resource: ShiftResource, primaryFieldValue: row.uuid, mode: 'view' })
                    }
                >
                    {code}
                </button>
            ),
        },
        {
            title: 'ТЗ',
            dataIndex: 'vehicleLabel',
            key: 'vehicleLabel',
            render: (value: string | null) => value ?? '—',
        },
        {
            title: 'Одометр',
            key: 'odometer',
            render: (_: unknown, row: MileageShift) => (
                <span className={cx(styles, 'num')}>
                    {formatKm(row.startOdometer)} → {formatKm(row.endOdometer)}
                </span>
            ),
        },
        {
            title: 'Пробіг за зміною',
            dataIndex: 'mileage',
            key: 'mileage',
            align: 'right' as const,
            render: km,
        },
        {
            title: 'Пробіг за Wialon',
            dataIndex: 'wialonMileage',
            key: 'wialonMileage',
            align: 'right' as const,
            render: km,
        },
        {
            title: 'Відхилення',
            dataIndex: 'deviation',
            key: 'deviation',
            align: 'right' as const,
            render: (value: number | null, row: MileageShift) =>
                value === null ? (
                    <span className={cx(styles, 'faint')}>немає даних</span>
                ) : (
                    <span
                        className={cx(styles, 'num')}
                        title={
                            row.deviationPercent === null && value !== 0
                                ? 'Пробіг Wialon 0 — відсоток не рахується; фільтр у % вважає таке відхилення нескінченним'
                                : undefined
                        }
                    >
                        {formatKmDelta(value)}
                        {row.deviationPercent !== null && (
                            <span className={cx(styles, 'faint')}> · {formatPercentDelta(row.deviationPercent)}</span>
                        )}
                    </span>
                ),
        },
        {
            title: 'Статус контролю',
            dataIndex: 'controlStatus',
            key: 'controlStatus',
            render: (value: MileageShift['controlStatus']) => {
                const option = MILEAGE_DEVIATION_OPTIONS.find((item) => item.value === value)
                return option ? <StatusBadge option={option} /> : <span className={cx(styles, 'faint')}>—</span>
            },
        },
    ]

    return (
        <div className={cx(styles, 'drill')}>
            <p className={cx(styles, 'drillTitle')}>Зміни водія</p>
            <Table<MileageShift>
                className="wl-table"
                size="small"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={items}
                pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'Змін немає' }}
            />
            {items.length >= LIMIT && (
                <div className={cx(styles, 'drillNote')}>Показано перші {LIMIT} змін — звузьте фільтри.</div>
            )}
        </div>
    )
}
